/**
 * KI-Coach (27.10.2026, Timo: „mach 1–4“, Punkt 3).
 *
 * Ein Sprachmodell — ab Werk das eigene (Ollama) — schaut auf eine **knappe
 * Zusammenfassung** der letzten ein bis vier Wochen und antwortet mit
 * Beobachtungen, Fragen zur Reflexion und höchstens einem Vorschlag im Rahmen
 * der eigenen Pläne. Es protokolliert nichts und verordnet nichts.
 *
 * **Ende-zu-Ende (E21):** Der Server kann Verschlüsseltes nicht lesen. Deshalb
 * stellt **das Gerät** die Zusammenfassung zusammen (`coachSummary`, hier als
 * reine Funktion), zeigt sie vor dem Senden vollständig an und schickt sie nur
 * nach Bestätigung. Der Server prüft sie gegen `CoachSummarySchema` (strikt:
 * nur Zahlen, Wochentage, Uhrzeiten und kurze Übungsnamen — kein Feld für
 * Freitext), reicht sie ans Modell und vergisst sie.
 *
 * **Nie ans Modell:** Gedanken, Tagebuch-Notizen, Bildunterschriften, Fotos,
 * Körpergewicht und Maße, Supplement-Namen und -Dosen, Namen anderer Personen,
 * Termine. Fehlende Werte sind `null` — nie 0; das Modell bekommt das gesagt.
 *
 * Die Antwort läuft durch `checkCoachAdvice`: Was nach Diagnose, Dosis,
 * Kalorien-/Gewichtsziel, Abnehmen, Bewertung des Körpers, Medizin oder
 * Beschämung klingt, fällt heraus. Den Hinweis auf ärztlichen Rat schreibt nie
 * das Modell, sondern die App (`careHint` schaltet nur ihren festen Text an).
 */

import { z } from 'zod';
import type { Module } from './modules.js';
import { activeModules } from './modules.js';
import { assignPlanDays, trainingOccurrences } from './schedule.js';
import type { TrainingDay, TrainingSlot } from './schedule.js';
import { addDays, isoDateInZone } from './time.js';
import type { IsoDate } from './time.js';

/** Wie weit der Coach zurückschaut — eine bis vier Wochen, Vorgabe zwei. */
export const COACH_WEEK_CHOICES = [1, 2, 3, 4] as const;
export type CoachWeeks = (typeof COACH_WEEK_CHOICES)[number];
export const COACH_DEFAULT_WEEKS: CoachWeeks = 2;

/** Höchstens so viele Übungen gehen mit — die häufigsten. */
export const COACH_MAX_EXERCISES = 8;
export const COACH_EXERCISE_NAME_MAX = 60;

/** Module, aus denen die Zusammenfassung schöpfen kann (E15). */
export const COACH_SECTIONS = ['training', 'nutrition', 'journal'] as const;
export type CoachSection = (typeof COACH_SECTIONS)[number];

// ---------------------------------------------------------------------------
// Die Zusammenfassung — was das Gerät sendet
// ---------------------------------------------------------------------------

const count = z.number().int().min(0).max(10_000);
const maybe = <T extends z.ZodType>(schema: T) => schema.nullable();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const weekday = z.number().int().min(1).max(7);
const scale = z.number().min(1).max(5);

/**
 * Strikt: Unbekannte Felder weist der Server ab. So kann keine ältere oder
 * fremde App Freitext (Gedanken, Notizen) unter einem neuen Namen mitschicken.
 */
export const CoachSummarySchema = z.strictObject({
  weeks: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  from: isoDate,
  to: isoDate,
  /** Abschnitte, die mitgehen — ein abgeschaltetes Modul fehlt (E15). */
  sections: z.array(z.enum(COACH_SECTIONS)).max(COACH_SECTIONS.length),
  training: maybe(
    z.strictObject({
      sessions: count,
      /** Einheiten je Woche, älteste zuerst. */
      sessionsPerWeek: z.array(count).max(4),
      /** Volumen (kg × Wdh. der Arbeitssätze) je Woche, älteste zuerst. */
      volumeKgPerWeek: z.array(z.number().int().min(0).max(10_000_000)).max(4),
      /** Geplante Trainingstermine — `null` ohne Trainingstage. */
      planned: maybe(count),
      done: maybe(count),
      missed: maybe(count),
      cancelled: maybe(count),
      /** Verpasste Termine nach Wochentag (ISO, 1 = Montag), nur > 0. */
      missedByWeekday: z.array(z.strictObject({ weekday, missed: count })).max(7),
      /** Die eigene Wochenregel — Grundlage für „Donnerstag verschieben?“. */
      trainingDays: z
        .array(z.strictObject({ weekday, time: z.string().regex(/^\d{2}:\d{2}$/) }))
        .max(14),
      exercises: z
        .array(
          z.strictObject({
            name: z.string().min(1).max(COACH_EXERCISE_NAME_MAX),
            sessions: count,
          }),
        )
        .max(COACH_MAX_EXERCISES),
      /** Eigene Bewertung der Einheiten (1–5). */
      avgRating: maybe(scale),
    }),
  ),
  nutrition: maybe(
    z.strictObject({
      days: count,
      daysLogged: count,
      /** Schnitt über die Tage mit Eintrag — `null` ohne Eintrag. */
      avgKcal: maybe(z.number().int().min(0).max(20_000)),
      avgProteinG: maybe(z.number().int().min(0).max(2_000)),
      /** Eigene Ziele — `null`, wenn keine gesetzt sind. */
      kcalTarget: maybe(z.number().int().min(0).max(20_000)),
      proteinTargetG: maybe(z.number().int().min(0).max(2_000)),
      proteinGoalDays: maybe(count),
      waterDaysLogged: count,
      avgWaterMl: maybe(z.number().int().min(0).max(20_000)),
      waterTargetMl: z.number().int().min(0).max(20_000),
      waterGoalDays: count,
    }),
  ),
  supplements: z.strictObject({
    due: count,
    taken: count,
    skipped: count,
  }),
  journal: maybe(
    z.strictObject({
      daysWithEntry: count,
      avgSleepHours: maybe(z.number().min(0).max(24)),
      /** Skalen 1–5 aus dem Tagesbefinden. */
      avgSleepQuality: maybe(scale),
      avgEnergy: maybe(scale),
      avgMood: maybe(scale),
      avgSoreness: maybe(scale),
    }),
  ),
});

export type CoachSummary = z.infer<typeof CoachSummarySchema>;

export interface CoachInput {
  /** Heute, Ortszeit — der letzte Tag des Zeitraums. */
  today: string;
  timeZone: string;
  now: Date;
  weeks: CoachWeeks;
  /** Kinder (E16) bekommen keinen Coach — die Funktion gibt dann `null`. */
  isChild: boolean;
  /** `profiles.modules` — `null` heißt alles an. */
  modules: readonly string[] | null;
  sessions: readonly {
    id: string;
    startedAt: string;
    rating?: number | null;
    deletedAt?: string | null;
  }[];
  sets: readonly {
    sessionId: string;
    exerciseId: string;
    weightKg: number | null;
    reps: number | null;
    isWarmup: boolean;
    deletedAt?: string | null;
  }[];
  exercises: readonly {
    id: string;
    name: string;
    variantLabel?: string | null;
    deletedAt?: string | null;
  }[];
  slots: readonly (TrainingSlot & { deletedAt?: string | null })[];
  trainingDays: readonly (TrainingDay & { deletedAt?: string | null })[];
  food: readonly { date: string; kcal: number; protein: number; deletedAt?: string | null }[];
  targets: { kcal: number; protein: number } | null;
  water: readonly { date: string; ml: number; deletedAt?: string | null }[];
  waterTargetMl: number;
  intakes: readonly {
    dueAt: string;
    acknowledgedAt: string | null;
    skippedAt: string | null;
    deletedAt?: string | null;
  }[];
  journal: readonly {
    date: string;
    sleepHours?: number | null;
    sleepQuality?: number | null;
    energy?: number | null;
    mood?: number | null;
    soreness?: number | null;
    deletedAt?: string | null;
  }[];
}

const live = <T extends { deletedAt?: string | null }>(rows: readonly T[]) =>
  rows.filter((row) => !row.deletedAt);

/** Schnitt der bekannten Werte — `null`, wenn keiner bekannt ist. Nie 0 für „fehlt“. */
function mean(values: readonly (number | null | undefined)[], digits = 0): number | null {
  const known = values.filter((value): value is number => typeof value === 'number');
  if (known.length === 0) return null;
  const factor = 10 ** digits;
  return (
    Math.round((known.reduce((sum, value) => sum + value, 0) / known.length) * factor) / factor
  );
}

/** Übungsname kurz und ohne Steuerzeichen — mehr als ein Name ist es nicht. */
export function coachExerciseName(name: string, variant?: string | null): string {
  const full = variant ? `${name} (${variant})` : name;
  const clean = full
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return clean.slice(0, COACH_EXERCISE_NAME_MAX).trim() || '?';
}

/**
 * Die Zusammenfassung für den Coach — auf dem Gerät gerechnet, aus
 * entschlüsselten lokalen Zeilen. `null` für Kinder (E16).
 *
 * Abschnitte folgen den Modulen (E15): ohne Training kein Trainingsabschnitt,
 * ohne Ernährung keine kcal und kein Wasser, ohne Tagebuch kein Befinden.
 * Supplements gehören keinem Modul und gehen als drei Zahlen mit.
 */
export function coachSummary(input: CoachInput): CoachSummary | null {
  if (input.isChild) return null;
  const on = activeModules(input.modules);
  const sections = COACH_SECTIONS.filter((section) => on.includes(section as Module));
  const to = input.today;
  const from = addDays(to as IsoDate, -(7 * input.weeks - 1));
  const inRange = (date: string) => date >= from && date <= to;
  const weekIndex = (date: string) =>
    Math.floor(
      (Date.parse(`${date}T12:00:00Z`) - Date.parse(`${from}T12:00:00Z`)) / (7 * 86_400_000),
    );

  // --- Training -----------------------------------------------------------
  let training: CoachSummary['training'] = null;
  if (sections.includes('training')) {
    const sessionDay = new Map<string, string>();
    const sessionsPerWeek = Array.from({ length: input.weeks }, () => 0);
    const volumeKgPerWeek = Array.from({ length: input.weeks }, () => 0);
    const ratings: (number | null | undefined)[] = [];
    for (const session of live(input.sessions)) {
      const day = isoDateInZone(new Date(session.startedAt), input.timeZone);
      if (!inRange(day)) continue;
      sessionDay.set(session.id, day);
      sessionsPerWeek[weekIndex(day)]! += 1;
      ratings.push(session.rating);
    }
    const exerciseSessions = new Map<string, Set<string>>();
    for (const set of live(input.sets)) {
      const day = sessionDay.get(set.sessionId);
      if (!day || set.isWarmup) continue;
      const sessions = exerciseSessions.get(set.exerciseId) ?? new Set<string>();
      sessions.add(set.sessionId);
      exerciseSessions.set(set.exerciseId, sessions);
      if (set.weightKg !== null && set.reps !== null) {
        volumeKgPerWeek[weekIndex(day)]! += set.weightKg * set.reps;
      }
    }
    const names = new Map(
      live(input.exercises).map((exercise) => [
        exercise.id,
        coachExerciseName(exercise.name, exercise.variantLabel),
      ]),
    );
    const exercises = [...exerciseSessions.entries()]
      .flatMap(([id, sessions]) => {
        const name = names.get(id);
        return name ? [{ name, sessions: sessions.size }] : [];
      })
      .sort((a, b) => b.sessions - a.sessions || a.name.localeCompare(b.name))
      .slice(0, COACH_MAX_EXERCISES);

    // Termine: dieselbe Regel wie App und Scheduler (verpasst = eine Stunde
    // nach Ende ohne Einheit; abgesagt ist nie verpasst).
    const slots = live(input.slots);
    const occurrences = trainingOccurrences(
      slots,
      live(input.trainingDays),
      from,
      to,
      input.timeZone,
    );
    const flagged = assignPlanDays(occurrences, {
      planDayIds: [],
      lastPlanDayId: null,
      trainedDates: new Set(sessionDay.values()),
      now: input.now,
      timeZone: input.timeZone,
    });
    const hasPlan = occurrences.length > 0;
    const missedBy = new Map<number, number>();
    for (const entry of flagged) {
      if (!entry.missed) continue;
      const day = new Date(`${entry.date}T12:00:00Z`).getUTCDay() || 7;
      missedBy.set(day, (missedBy.get(day) ?? 0) + 1);
    }
    training = {
      sessions: sessionDay.size,
      sessionsPerWeek,
      volumeKgPerWeek: volumeKgPerWeek.map((value) => Math.round(value)),
      planned: hasPlan ? flagged.filter((entry) => entry.status === 'planned').length : null,
      done: hasPlan ? flagged.filter((entry) => entry.done).length : null,
      missed: hasPlan ? flagged.filter((entry) => entry.missed).length : null,
      cancelled: hasPlan ? flagged.filter((entry) => entry.status === 'cancelled').length : null,
      missedByWeekday: [...missedBy.entries()]
        .sort((a, b) => a[0] - b[0])
        .map(([weekday, missed]) => ({ weekday, missed })),
      trainingDays: slots
        .filter((slot) => slot.active)
        .map((slot) => ({ weekday: slot.weekday, time: slot.time }))
        .sort((a, b) => a.weekday - b.weekday || a.time.localeCompare(b.time))
        .slice(0, 14),
      exercises,
      avgRating: mean(ratings, 1),
    };
  }

  // --- Ernährung und Wasser ----------------------------------------------
  let nutrition: CoachSummary['nutrition'] = null;
  if (sections.includes('nutrition')) {
    const perDay = new Map<string, { kcal: number; protein: number }>();
    for (const entry of live(input.food)) {
      if (!inRange(entry.date)) continue;
      const day = perDay.get(entry.date) ?? { kcal: 0, protein: 0 };
      day.kcal += entry.kcal;
      day.protein += entry.protein;
      perDay.set(entry.date, day);
    }
    const days = [...perDay.values()];
    const waterPerDay = new Map<string, number>();
    for (const entry of live(input.water)) {
      if (!inRange(entry.date)) continue;
      waterPerDay.set(entry.date, (waterPerDay.get(entry.date) ?? 0) + entry.ml);
    }
    const water = [...waterPerDay.values()];
    nutrition = {
      days: 7 * input.weeks,
      daysLogged: days.length,
      avgKcal: mean(days.map((day) => day.kcal)),
      avgProteinG: mean(days.map((day) => day.protein)),
      kcalTarget: input.targets ? Math.round(input.targets.kcal) : null,
      proteinTargetG: input.targets ? Math.round(input.targets.protein) : null,
      proteinGoalDays: input.targets
        ? days.filter((day) => day.protein >= input.targets!.protein).length
        : null,
      waterDaysLogged: water.length,
      avgWaterMl: mean(water),
      waterTargetMl: Math.round(input.waterTargetMl),
      waterGoalDays: water.filter((ml) => ml >= input.waterTargetMl).length,
    };
  }

  // --- Supplements: nur, was schon fällig war ----------------------------
  const supplements = { due: 0, taken: 0, skipped: 0 };
  for (const intake of live(input.intakes)) {
    const at = new Date(intake.dueAt);
    if (at > input.now || !inRange(isoDateInZone(at, input.timeZone))) continue;
    supplements.due += 1;
    if (intake.acknowledgedAt) supplements.taken += 1;
    else if (intake.skippedAt) supplements.skipped += 1;
  }

  // --- Befinden (Tagebuch), nur Zahlen ------------------------------------
  let journal: CoachSummary['journal'] = null;
  if (sections.includes('journal')) {
    const rows = live(input.journal).filter((row) => inRange(row.date));
    const withValue = rows.filter((row) =>
      [row.sleepHours, row.sleepQuality, row.energy, row.mood, row.soreness].some(
        (value) => typeof value === 'number',
      ),
    );
    journal = {
      daysWithEntry: new Set(withValue.map((row) => row.date)).size,
      avgSleepHours: mean(
        rows.map((row) => row.sleepHours),
        1,
      ),
      avgSleepQuality: mean(
        rows.map((row) => row.sleepQuality),
        1,
      ),
      avgEnergy: mean(
        rows.map((row) => row.energy),
        1,
      ),
      avgMood: mean(
        rows.map((row) => row.mood),
        1,
      ),
      avgSoreness: mean(
        rows.map((row) => row.soreness),
        1,
      ),
    };
  }

  return {
    weeks: input.weeks,
    from,
    to,
    sections: [...sections],
    training,
    nutrition,
    supplements,
    journal,
  };
}

/** Gibt es überhaupt etwas zu besprechen? Ohne Daten bietet die App nichts an. */
export function coachHasData(summary: CoachSummary): boolean {
  return (
    (summary.training !== null &&
      (summary.training.sessions > 0 || (summary.training.planned ?? 0) > 0)) ||
    (summary.nutrition !== null &&
      (summary.nutrition.daysLogged > 0 || summary.nutrition.waterDaysLogged > 0)) ||
    summary.supplements.due > 0 ||
    (summary.journal !== null && summary.journal.daysWithEntry > 0)
  );
}

// ---------------------------------------------------------------------------
// Die Antwort — was das Modell liefern muss und was die App bekommt
// ---------------------------------------------------------------------------

export const COACH_TEXT_MAX = 400;
const text = z.string().trim().min(1).max(COACH_TEXT_MAX);

/** Rohe Antwort des Modells (vor der Prüfung). Vorschlag leer heißt keiner. */
export const CoachReplySchema = z.object({
  observations: z.array(text).min(1).max(6),
  questions: z.array(text).min(1).max(4),
  suggestion: z.string().trim().max(COACH_TEXT_MAX),
  careHint: z.boolean(),
});
export type CoachReply = z.infer<typeof CoachReplySchema>;

/** Geprüfte Antwort, wie die App sie anzeigt. */
export const CoachAdviceSchema = z.object({
  observations: z.array(text).min(1).max(4),
  questions: z.array(text).max(2),
  suggestion: text.nullable(),
  /** Die App zeigt dann ihren festen Hinweis auf ärztlichen Rat. */
  careHint: z.boolean(),
  /** So viele Sätze hat die Prüfung herausgenommen — die App sagt es. */
  removed: z.number().int().min(0),
});
export type CoachAdvice = z.infer<typeof CoachAdviceSchema>;

/** Gründe, die der Server bei einem gescheiterten Coach-Aufruf nennt. */
export const COACH_REASONS = [
  /** Kein Anbieter eingerichtet. */
  'disabled',
  /** Kinderkonto (E16). */
  'child',
  /** Die Zusammenfassung passt nicht zum Schema (z. B. ein Feld zu viel). */
  'summary',
  /** Das Modell hat abgelehnt. */
  'refused',
  /** Antwort unvollständig oder kein JSON. */
  'invalid',
  /** Nach der Prüfung bleibt keine Beobachtung übrig. */
  'unsafe',
  /** Nicht erreichbar, überlastet oder zu langsam. */
  'upstream',
  /** Zu viele Aufrufe. */
  'rate',
] as const;
export type CoachReason = (typeof COACH_REASONS)[number];

/**
 * Was der Coach nie sagt (Prompt **und** diese Prüfung). Je Regel Muster in
 * Deutsch und Englisch, ohne Groß/Klein. Lieber ein guter Satz zu viel
 * herausgenommen als ein verordnender durchgelassen — die App sagt, dass
 * etwas fehlt.
 */
export const COACH_FORBIDDEN: Record<string, readonly RegExp[]> = {
  // Diagnosen und Krankheitsbilder.
  diagnosis: [
    /diagnos/i,
    /störung|disorder/i,
    /syndrom/i,
    /\bdepress/i,
    /anorex|bulim|binge|essanfall|orthorex/i,
    /mangel|deficien/i,
    /\bleidest\b|\bleiden an\b|suffer(?:ing)? from/i,
    /\b(?:krankheit|erkrankung|disease|illness)\b/i,
  ],
  // Dosierungen und Einnahme-Anweisungen.
  dosage: [/\d+(?:[.,]\d+)?\s?(?:mg|µg|mcg|ug|i\.?e\.?|iu)\b/i, /dosis|dosier|\bdose|dosage/i],
  // Medizinische Empfehlungen — der Hinweis auf ärztlichen Rat kommt von der App.
  medical: [
    /medikament|medication|medicine|arznei|\bpille|\bpill\b|tablette/i,
    /therap|behandl|treatment/i,
    /\barzt|ärzt|doctor|physician|klinik|clinic/i,
  ],
  // Kalorien-, Protein- oder Gewichtsziele vorgeben oder ändern.
  targets: [
    /(?:erhöh|senk|reduzier|steiger|änder|anpass|setz|verringer|raise|lower|reduce|increase|change|adjust|set)\w*\b[^.!?\n]{0,40}\b(?:kalorien|kcal|proteinziel|kalorienziel|zielwert|ziel|target|goal)/i,
    /(?:kalorien|kcal|proteinziel|kalorienziel|zielwert|target|goal)\w*\b[^.!?\n]{0,40}\b(?:erhöh|senk|reduzier|steiger|änder|anpass|verringer|raise|lower|reduce|increase|change|adjust)/i,
    /\b(?:iss|esse|eat)\b[^.!?\n]{0,20}\b(?:weniger|less|nichts|nothing)\b/i,
    /\bdefizit|deficit|surplus|kalorienüberschuss/i,
    /\bfast(?:en|ing)\b|intervallfast/i,
  ],
  // Abnehmen, Zunehmen, Diät — ein Körperziel gibt nie der Coach vor.
  weightLoss: [
    /abnehm|abgenommen|zunehm|zugenommen|abspeck/i,
    /(?:lose|losing|lost|gain|gaining)\s+weight|weight\s*(?:loss|gain)/i,
    /\bdiät|\bdiet\b|\bdieting/i,
    /körpergewicht|body\s?weight/i,
  ],
  // Körperwerte als Bewertung.
  body: [
    /\bbmi\b|übergewicht|untergewicht|overweight|underweight|obes|adipös/i,
    /körperfett|body\s?fat|\bfigur\b|\bschlank|\bdick\b|\bdünn\b|\bfat\b|\bskinny\b|\bthin\b/i,
  ],
  // Beschämender Ton.
  shaming: [
    /\bfaul|\blazy\b|versag|\bfail(?:ed|ure)\b|enttäusch|disappoint|schäm|\bshame|undisziplin|undisciplined|\bschwach\b|\bweak\b|\bpathetic\b|erbärmlich/i,
  ],
};

/** Die erste Regel, gegen die ein Satz verstößt — `null`, wenn er durchgeht. */
export function coachForbidden(sentence: string): string | null {
  for (const [rule, patterns] of Object.entries(COACH_FORBIDDEN)) {
    if (patterns.some((pattern) => pattern.test(sentence))) return rule;
  }
  return null;
}

/**
 * Antwort des Modells prüfen: Verbotenes fällt heraus, zu viele Sätze werden
 * gekürzt (2–4 Beobachtungen, 1–2 Fragen, höchstens ein Vorschlag). Bleibt
 * keine Beobachtung übrig, ist die ganze Antwort unbrauchbar (`null`).
 * `rules` nennt die verletzten Regeln — fürs Logbuch, ohne den Text.
 */
export function checkCoachAdvice(reply: CoachReply): {
  advice: CoachAdvice | null;
  rules: string[];
} {
  const rules: string[] = [];
  let removed = 0;
  const keep = (sentence: string) => {
    const rule = coachForbidden(sentence);
    if (rule === null) return true;
    rules.push(rule);
    removed += 1;
    return false;
  };
  const observations = reply.observations.filter(keep).slice(0, 4);
  const questions = reply.questions.filter(keep).slice(0, 2);
  const suggestion = reply.suggestion !== '' && keep(reply.suggestion) ? reply.suggestion : null;
  if (observations.length === 0) return { advice: null, rules };
  return {
    advice: { observations, questions, suggestion, careHint: reply.careHint, removed },
    rules,
  };
}
